import {CatalogFeature, isPaidTariff} from './catalogApi'
import {fa, needsContact, QuotaScope, tariffParts} from './serviceApi'

// Whether an operation about to start costs anything, for the service card at its start (user request 2026-10-19:
// «اینجاها که عملیات الانش رایگان هست دیگه این توضیحات نباید بیاد جلو چشمش … حالت بازشو داشته باشه … فقط سهمیه
// رایگانش و چقدر مصرف کرده و چقدر مونده باشه و قیمتش»). The panel's judgement only decides what is shown first: a
// fee the server would take is always shown for confirmation before it is taken.

export type OperationAllowance = {
  /** Nothing to pay for this operation as far as the panel knows: the card folds into one line. */
  free: boolean
  /** The free allowance of the tariff, in its scope; undefined when it has none. */
  quota?: number
  scope?: QuotaScope
  /** What counts against the allowance now (this operation, today, this month or in total); undefined when unknown. */
  used?: number
  /** What is left of the allowance; undefined when unknown. */
  left?: number
  /** The daily limit is reached: the operation would be refused. */
  limitReached: boolean
  /** One line on why it is free («۴۰ مورد رایگانِ امروز باقی مانده است»). */
  summary: string
}

/**
 * <paramref name="uses"/>: how many uses the operation makes (the invoices in an export), when the page knows them;
 * <paramref name="used"/>: what already counts against a total allowance (the active users, the print templates).
 */
export const operationAllowance = (
  item: CatalogFeature,
  {uses, used: usedTotal}: {uses?: number; used?: number} = {}
): OperationAllowance => {
  const tariff = item.tariff
  const parts = tariffParts(tariff)
  const quota = tariff.freeQuota && tariff.freeQuota > 0 ? tariff.freeQuota : undefined
  const scope: QuotaScope | undefined = quota ? tariff.freeQuotaScope || 'total' : undefined
  const limitReached = Boolean(tariff.dailyLimit && (tariff.usedToday || 0) >= tariff.dailyLimit)
  const count = uses !== undefined && uses >= 0 ? uses : undefined

  let used: number | undefined
  if (scope === 'operation') used = count
  else if (scope === 'day') used = tariff.usedToday ?? 0
  else if (scope === 'month') used = tariff.usedThisMonth ?? 0
  else if (scope === 'total') used = usedTotal
  let left: number | undefined
  if (quota !== undefined) {
    if (scope === 'operation') left = Math.max(0, quota - (count || 0))
    else if (used !== undefined) left = Math.max(0, quota - used)
  }

  const base = {quota, scope, used, left, limitReached}
  if (limitReached) return {...base, free: false, summary: ''}

  // Turned off, ended, or still to be turned on: the card's own words matter.
  if (tariff.switchedOff || tariff.expired) return {...base, free: false, summary: ''}
  const waitsForActivation = !item.active && parts.activation > 0 && !needsContact(tariff)
  if (waitsForActivation) return {...base, free: false, summary: ''}

  // Nothing per use (a free tariff, a subscription already paid) or nothing for this business (its free list, a gift,
  // a package): free whatever the allowance.
  const perUse = parts.use > 0 || Boolean(tariff.usePercent)
  // «ترکیبی» on the subscription (2026-10-20): every use within it is free.
  if (item.active && tariff.method === 'subscription') return {...base, free: true, summary: 'در اشتراک شما رایگان است.'}
  if (!isPaidTariff(tariff) && !needsContact(tariff)) return {...base, free: true, summary: 'این عملیات رایگان است.'}
  if (!perUse && item.active) return {...base, free: true, summary: 'این عملیات هزینهٔ جداگانه ندارد.'}
  if (perUse && item.active && item.useFee === 0) return {...base, free: true, summary: 'این عملیات برای این کسب‌وکار رایگان است.'}

  // The free allowance: a «تماس بگیرید» service keeps it too, without an access.
  if (quota === undefined) return {...base, free: false, summary: ''}
  const wanted = count ?? 1
  if (scope === 'operation') {
    if (count !== undefined && count > quota) return {...base, free: false, summary: ''}
    return {
      ...base,
      free: true,
      summary:
        count !== undefined
          ? `${fa(count)} مورد از ${fa(quota)} مورد رایگانِ هر عملیات؛ این عملیات رایگان است.`
          : `${fa(quota)} مورد اول هر عملیات رایگان است.`,
    }
  }
  if (left === undefined) return {...base, free: false, summary: ''}
  if (left < wanted) return {...base, free: false, summary: ''}
  const when = scope === 'day' ? 'امروز ' : scope === 'month' ? 'این ماه ' : ''
  return {...base, free: true, summary: `${when}${fa(left)} مورد رایگان باقی مانده است.`}
}

/** What the used figure counts, in the card's details. */
export const usedLabel = (scope?: QuotaScope) =>
  scope === 'operation' ? 'در این عملیات' : scope === 'day' ? 'مصرف امروز' : scope === 'month' ? 'مصرف این ماه' : 'استفاده‌شده'
